import { Card } from "@/components/ui/card";
import { Award, ClipboardCheck, Clock, X } from "lucide-react";

const fmtClock = (s) =>
  `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

const VITAL_META = {
  hr: { label: "Heart rate", color: "#39FF14" },
  rr: { label: "Resp rate", color: "#FFEA00" },
  spo2: { label: "SpO₂", color: "#00FFFF" },
  temp: { label: "Temperature", color: "#FF8C42" },
  map: { label: "MAP", color: "#FF8C42" },
};

export function DebriefCard({ debrief, onDismiss }) {
  const gradeColor =
    debrief.score >= 80
      ? "text-emerald-600"
      : debrief.score >= 60
      ? "text-amber-600"
      : "text-red-600";
  const timeline = [
    ...debrief.events.map((e) => ({ t: e.t, kind: e.kind, label: e.label })),
  ].sort((a, b) => a.t - b.t);

  return (
    <Card className="border-emerald-200 p-6 mt-5 shadow-lg" data-testid="anaesthesia-debrief">
      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-emerald-700 font-semibold">
          <ClipboardCheck className="w-4 h-4" />
          <span>Session debrief</span>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          data-testid="debrief-dismiss-btn"
          className="text-slate-400 hover:text-slate-700 transition-colors"
          aria-label="Dismiss debrief"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Grade + headline metrics */}
        <div className="flex items-center gap-5">
          <div
            className={`flex flex-col items-center justify-center w-24 h-24 rounded-2xl border-4 ${
              debrief.score >= 80
                ? "border-emerald-300 bg-emerald-50"
                : debrief.score >= 60
                ? "border-amber-300 bg-amber-50"
                : "border-red-300 bg-red-50"
            }`}
            data-testid="debrief-grade"
          >
            <Award className={`w-5 h-5 ${gradeColor}`} />
            <span className={`text-4xl font-bold leading-none ${gradeColor}`}>
              {debrief.grade}
            </span>
          </div>
          <div>
            <div className="text-3xl font-bold text-slate-900" data-testid="debrief-score">
              {debrief.score}
              <span className="text-base text-slate-400 font-medium">/100</span>
            </div>
            <div className="text-sm text-slate-500 mt-0.5">
              {fmtClock(debrief.elapsed)} anaesthesia time
            </div>
            <div className="text-xs text-slate-500 mt-2 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-slate-400" />
              {debrief.avgResponse != null
                ? `Avg. response ${fmtClock(debrief.avgResponse)}`
                : "No complications to manage"}
            </div>
          </div>
        </div>

        {/* Time-in-range per vital */}
        <div data-testid="debrief-stability">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">
            Time in range · {debrief.stabilityPct}% overall
          </div>
          <div className="space-y-2">
            {Object.entries(debrief.perVital).map(([k, pct]) => (
              <div key={k} data-testid={`debrief-vital-${k}`}>
                <div className="flex items-center justify-between text-xs mb-0.5">
                  <span className="text-slate-600 font-medium">{VITAL_META[k].label}</span>
                  <span className="font-mono-clinical font-semibold text-slate-800">{pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${pct}%`,
                      backgroundColor: pct >= 80 ? "#10b981" : pct >= 50 ? "#f59e0b" : "#ef4444",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Complication handling */}
        <div data-testid="debrief-complications">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">
            Complications · {debrief.resolved}/{debrief.triggered} resolved
          </div>
          {debrief.complications.length === 0 ? (
            <div className="text-sm text-slate-500 border-2 border-dashed border-slate-200 rounded-md p-4 text-center">
              No complications were triggered this session.
            </div>
          ) : (
            <div className="space-y-1.5">
              {debrief.complications.map((c, i) => (
                <div
                  key={`${c.key}-${i}`}
                  data-testid={`debrief-complication-${i}`}
                  className={`flex items-center justify-between gap-2 text-xs rounded-md px-3 py-2 border ${
                    c.resolved != null
                      ? "border-emerald-200 bg-emerald-50"
                      : "border-red-200 bg-red-50"
                  }`}
                >
                  <span className="font-semibold text-slate-800">{c.label}</span>
                  <span className="font-mono-clinical text-slate-500">
                    {c.resolved != null
                      ? `resolved in ${fmtClock(c.resolved - c.onset)}`
                      : "unresolved"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Event timeline */}
      {timeline.length > 0 && (
        <div className="mt-6" data-testid="debrief-timeline">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
            Event timeline
          </div>
          <div className="flex flex-col gap-1 max-h-56 overflow-y-auto pr-1">
            {timeline.map((e, i) => (
              <div
                key={i}
                className="flex items-center gap-3 text-xs py-1 border-b border-slate-100 last:border-0"
              >
                <span className="font-mono-clinical text-slate-400 w-12 shrink-0">
                  {fmtClock(e.t)}
                </span>
                <span
                  className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                    e.kind === "complication"
                      ? "bg-red-500"
                      : e.kind === "drug"
                      ? "bg-[#0047AB]"
                      : "bg-emerald-500"
                  }`}
                />
                <span className="text-slate-700">{e.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
